<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import LeadsAPI from 'dashboard/api/leads';

const props = defineProps({
  leadId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const events = ref([]);
const isLoading = ref(false);

const load = async () => {
  if (!props.leadId) return;
  isLoading.value = true;
  try {
    const { data } = await LeadsAPI.events(props.leadId);
    events.value = data?.payload ?? data ?? [];
  } catch (error) {
    events.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.leadId, load);
onMounted(load);

const formatDate = ts => {
  try {
    // TODO locale dinámico (P3)
    return new Intl.DateTimeFormat('es-EC', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(ts * 1000));
  } catch (error) {
    return '';
  }
};

const DOT_CLASS_MAP = {
  created: 'bg-n-teal-9',
  moved: 'bg-n-blue-9',
  value_changed: 'bg-n-teal-9',
  assignee_changed: 'bg-n-brand',
  archived: 'bg-n-amber-9',
  restored: 'bg-n-teal-9',
};
const eventDotClass = event =>
  DOT_CLASS_MAP[event.event_type] || 'bg-n-slate-8';

const eventText = event => {
  switch (event.event_type) {
    case 'created':
      return t('LEADS.TIMELINE.CREATED', { stage: event.to_stage });
    case 'moved':
      return t('LEADS.TIMELINE.MOVED', {
        from: event.from_stage,
        to: event.to_stage,
      });
    case 'value_changed':
      return t('LEADS.TIMELINE.VALUE_CHANGED', {
        from: event.metadata?.from ?? '—',
        to: event.metadata?.to ?? '—',
      });
    case 'assignee_changed':
      return t('LEADS.TIMELINE.ASSIGNEE_CHANGED', {
        from: event.metadata?.from || t('LEADS.TIMELINE.NOBODY'),
        to: event.metadata?.to || t('LEADS.TIMELINE.NOBODY'),
      });
    case 'archived':
      return t('LEADS.TIMELINE.ARCHIVED');
    case 'restored':
      return t('LEADS.TIMELINE.RESTORED');
    default:
      return '';
  }
};

const eventMeta = event => {
  const date = formatDate(event.created_at);
  return event.user ? `${date} · ${event.user}` : date;
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <h4 class="text-sm font-semibold text-n-slate-12">
      {{ t('LEADS.TIMELINE.TITLE') }}
    </h4>
    <p v-if="!events.length && !isLoading" class="text-xs text-n-slate-10">
      {{ t('LEADS.TIMELINE.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-3">
      <li v-for="event in events" :key="event.id" class="flex gap-3">
        <span
          class="flex-shrink-0 mt-1.5 rounded-full size-2"
          :class="eventDotClass(event)"
        />
        <div class="flex flex-col">
          <span class="text-sm text-n-slate-12">{{ eventText(event) }}</span>
          <span class="text-xs text-n-slate-10">{{ eventMeta(event) }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>
